import React, { FC } from "react";
import { TextField } from "@mui/material";

interface PhoneNumberFieldProps {
    value?: string;
    onChange?: (value: string) => void;
    isEditing?: boolean;
    maxLength?: number;
    error?: boolean;
    helperText?: string;
}

const PhoneNumberField: FC<PhoneNumberFieldProps> = ({
    value = "",
    onChange,
    isEditing = false,
    maxLength = 13,
    error = false,
    helperText,
}) => {
    // 編集用の電話番号
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue = event.target.value;

        // "+" を先頭に許容し、それ以外は数字のみに制限
        if (inputValue.startsWith("+")) {
            inputValue = "+" + inputValue.slice(1).replace(/[^\d]/g, ""); // "+" を維持して数字のみ許容
        } else {
            inputValue = inputValue.replace(/[^\d]/g, ""); // 数字以外を削除
        }

        if (onChange) {
            onChange(inputValue.slice(0, maxLength)); // 最大文字数を制限
        }
    };

    // 表示用のフォーマット
    const formatPhoneNumber = (phoneNumber: string): string => {
        // 国際電話番号の判定
        if (phoneNumber.startsWith("+81") || phoneNumber.startsWith("81")) {
            // "81" を "+81" として処理し、日本国内形式に変換
            const isPrefixed = phoneNumber.startsWith("+81");
            const domesticPart = isPrefixed
                ? phoneNumber.slice(3).replace(/^0/, "") // "+81" の後の 0 を削除
                : phoneNumber.slice(2).replace(/^0/, ""); // "81" の後の 0 を削除

            const formattedDomestic = formatDomesticPhoneNumber(domesticPart);
            return isPrefixed ? `+81 ${formattedDomestic}` : `81 ${formattedDomestic}`;
        }

        return formatDomesticPhoneNumber(phoneNumber); // 国内番号のフォーマット
    };

    // 国内電話番号のフォーマット
    const formatDomesticPhoneNumber = (phoneNumber: string): string => {
        // 10桁の場合（国内固定電話・携帯電話）
        if (phoneNumber.length === 10) {
            if (phoneNumber.startsWith("03") || phoneNumber.startsWith("06")) {
                return `${phoneNumber.slice(0, 2)}-${phoneNumber.slice(2, 6)}-${phoneNumber.slice(6)}`;
            } else {
                return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
            }
        }

        // 11桁の場合（国内携帯電話）
        if (phoneNumber.length === 11) {
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 7)}-${phoneNumber.slice(7)}`;
        }

        return phoneNumber; // その他の桁数はそのまま返す
    };

    return isEditing ? (
        <TextField
            fullWidth
            value={value}
            onChange={handleChange}
            size="small"
            error={error}
            helperText={helperText}
            inputProps={{
                inputMode: "text", // 数字入力に加えて "+" を許容
                maxLength,
                pattern: "[+0-9]*", // "+" と数字のパターンを許容
            }}
        />
    ) : (
        <span>{formatPhoneNumber(value)}</span>
    );
};

export default PhoneNumberField;